/**
 * usePullStatus
 *
 * React hook that mirrors the live pull-engine snapshot (state, pages,
 * records, lastSyncedAt) plus the browser's online/offline signal. Used by
 * SyncStatusBadge to render the progress indicator in page headers.
 */

import { useEffect, useState } from "react";

import {
  getPullSnapshot,
  PullSnapshot,
  runPullCycle,
  subscribePullState,
} from "../sync/pull-engine";

export interface PullStatus extends PullSnapshot {
  isOnline: boolean;
  /** Manually trigger a pull cycle (e.g. from a "Sync Now" button). */
  syncNow: () => Promise<void>;
}

export function usePullStatus(): PullStatus {
  const [snapshot, setSnapshot] = useState<PullSnapshot>(() => getPullSnapshot());
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    const unsubscribe = subscribePullState(setSnapshot);

    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      unsubscribe();
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const syncNow = async () => { await runPullCycle(); };

  return { ...snapshot, isOnline, syncNow };
}
